import React, { type FC, type MouseEvent, useState } from 'react';
import classnames from 'classnames';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import { Cart } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';

import type { IPrice, IProductListItem } from 'api/types';
import { useGetCartsAll, usePostCartAddProductToCart } from 'api';
import { Availability, Link } from 'components/controls';
import { Modal } from 'components/controls';
import { AddCartForm } from 'components/containers';
import { useNotifications } from 'hooks';
import { useSelector } from 'store';
import { WiseLabel } from 'components/wise';

import styles from 'theme/pages/wise/WiseProduct/components/WiseProductItem/WiseProductItem.module.scss';

export type WiseProductItemProps = {
  product: IProductListItem;
  price?: IPrice | null;
  className?: string;
  onFavouriteClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

const WiseProductItem: FC<WiseProductItemProps> = ({
  product,
  price: priceProp,
  className,
  onFavouriteClick
}) => {
  const { t } = useTranslation();
  const { showWarningMessage } = useNotifications();
  const { currentCartId } = useSelector((state) => state.cart);
  const [isAddCartModal, setIsAddCartModal] = useState(false);
  const { refetch: refetchCartData } = useGetCartsAll({ enabled: false });
  const { mutate: addPositionsToCartMutate, isLoading: isAddingPositions } =
    usePostCartAddProductToCart({
      onSuccess: () => {
        refetchCartData();
      }
    });

  const info = product.product_basic_short_info;
  const url = info?.url_link ? `/${info.url_link}` : '/';
  const imageBlock =
    product.product_image_formatted_short_info ??
    (product as { product_image_short_info?: { images: { min?: string }[] } }).product_image_short_info;
  const imageUrl = imageBlock?.images?.[0]?.min;
  const labels = info?.labels ?? [];
  const stock =
    product.product_stock_formatted_short_info ?? product.product_stock_formatted_full_info;

  const unitId = product.product_unit_short_info?.default_unit_id || 1;
  const price =
    priceProp ??
    product.product_price_short_info?.prices?.find((p) => p.unit_id === unitId) ??
    product.product_price_short_info?.prices?.[0];

  const handleFavourite = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onFavouriteClick?.(e);
  };

  const handleAddToCart = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product?.id || !unitId) {
      showWarningMessage(t('Nie udało się dodać produktu do koszyka'));
      return;
    }

    if (!currentCartId) {
      setIsAddCartModal(true);
      return;
    }

    addPositionsToCartMutate({
      cart_id: currentCartId,
      products: [
        {
          product_id: product.id,
          unit_id: unitId,
          quantity: 1,
          warehouse_id: null
        }
      ]
    });
  };

  return (
    <div
      className={classnames(
        styles.root,
        className,
        'StylePath-Pages-Wise-WiseProduct-components-WiseProductItem'
      )}>
      <div className={styles.imageBlock}>
        <Link to={url} className={styles.imageLink} aria-label={info?.title}>
          <div
            className={styles.image}
            style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
          />
        </Link>
        <div className={styles.overlayTop}>
          <div className={styles.labels}>
            {labels.map((label) => (
              <WiseLabel key={`${label.type}-${label.name}`} label={label} />
            ))}
          </div>
          <button
            type="button"
            className={styles.favouriteBtn}
            aria-label={t('Dodaj do ulubionych')}
            onClick={handleFavourite}>
            <FavoriteBorder fontSize="small" />
          </button>
        </div>
      </div>

      <Link to={url} className={styles.body}>
        {info?.title ? <p className={styles.title}>{info.title}</p> : null}

        {(info?.index || info?.producer_name) && (
          <div className={styles.additionalInfo}>
            {info?.index ? (
              <div>
                <Trans>Indeks</Trans>: {info.index}
              </div>
            ) : null}
            {info?.producer_name ? (
              <div>
                <Trans>Producent</Trans>: {info.producer_name}
              </div>
            ) : null}
          </div>
        )}

        {stock ? (
          <div className={styles.availabilitySlot}>
            <Availability stock={stock} />
          </div>
        ) : null}

        {price ? (
          <div className={styles.priceContainer}>
            <div className={styles.priceNetRow}>
              <span className={styles.priceNetValue}>{price.price_net_formatted}</span>
              <span className={styles.priceNetSuffix}>
                <Trans>netto</Trans>
              </span>
            </div>
            <div className={styles.priceGrossRow}>
              {price.price_gross_formatted} <Trans>brutto</Trans>
            </div>
          </div>
        ) : null}
      </Link>

      <button
        type="button"
        className={styles.addToCartBtn}
        aria-label={t('Dodaj do koszyka')}
        disabled={isAddingPositions}
        onClick={handleAddToCart}>
        <Cart aria-hidden />
      </button>

      {isAddCartModal && (
        <Modal title={t('Nowy koszyk')} onClose={() => setIsAddCartModal(false)}>
          <AddCartForm
            onCancel={() => setIsAddCartModal(false)}
            onSuccess={(cartId: number) => {
              addPositionsToCartMutate({
                cart_id: cartId,
                products: [
                  {
                    product_id: product.id,
                    unit_id: unitId,
                    quantity: 1,
                    warehouse_id: null
                  }
                ]
              });
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default WiseProductItem;
